import { readThemeSettingsResult } from '@sites/site-theme';

import { type EContentSources, EContentSourcesMLP } from '@common/enums';
import type { SiteContentEntity } from '@common/types/site-content.type';

/** A theme as the site stores it: the settings blob and the font imports written beside it. */
export type StoredTheme = { settings: string; styles: string | null };

/**
 * Whether mini-landing pages read this site's theme too. They read it with a bare parse in the old
 * shape, so a theme this editor rewrites in v0 — without the `root` they keep their background in —
 * takes their editor and their published pages down.
 */
export function sharesThemeWithMiniLanding(
    pages: readonly { content_source_id?: EContentSources }[] | null | undefined
): boolean {
    return (pages ?? []).some(({ content_source_id }) =>
        (EContentSourcesMLP as readonly EContentSources[]).includes(content_source_id)
    );
}

/**
 * The stored theme, when a save has to write it back as it was, because the user has not changed
 * the theme and either:
 * - the editor could read it only in part — serializing what it holds would replace the rest with
 *   defaults on the first unrelated edit, for good;
 * - mini-landing pages share it — see {@link sharesThemeWithMiniLanding}.
 *
 * `null` when the editor's own theme is the one to write.
 */
export function themeToPreserve(
    siteContent: SiteContentEntity | null | undefined,
    isEdited: boolean,
    isSharedWithMiniLanding: boolean = false
): StoredTheme | null {
    if (isEdited || !siteContent) {
        return null;
    }

    // The same source the editor read the theme from — see `getSiteContentThemeSettings`.
    const fromDraft = Boolean(siteContent.draft_theme_settings);
    const settings = fromDraft ? siteContent.draft_theme_settings : siteContent.theme_settings;

    if (!settings) {
        return null;
    }

    if (!isSharedWithMiniLanding && readThemeSettingsResult(settings).status === 'ok') {
        return null;
    }

    return {
        settings,
        styles: (fromDraft ? siteContent.draft_theme_settings_style : siteContent.theme_settings_style) ?? null,
    };
}
